/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import Container from '@/components/Container';
import AnimatedFadeInComponent from '@/components/AnimatedFadeInComponent';
import React from 'react';
import DefaultContentTemplateEmbedded from '@/components/DefaultContentTemplate/Embedded';
import { GENERATED } from '@edwin-group/people-graphql';
import type { Course, Enum_Like_Type, KnowledgeBase } from '@edwin-group/people-graphql';
import TitleWithActions from '@/components/TitleWithActions';
import { useResponsiveImage } from '@/utils/useResponsiveImage';

interface TitleWithActionsProps {
  entity: Course | KnowledgeBase;
  entityId: string;
  category: GENERATED.Enum_Bookmark_Category;
  moduleId?: string;
  lessonId?: string;
  like?: boolean;
  favorite?: boolean;
  type?: Enum_Like_Type;
  textSize?: string;
  knowledgeBaseTypeId?: string;
}

interface HighlightProps {
  title: string;
  sectionTitle?: string;
  description: string;
  image?: string;
  video?: string;
  button?: React.ReactNode;
  imageAlt?: string;
  showTitleWithActions?: boolean;
  titleWithActionsProps?: TitleWithActionsProps;
  backButton?: React.ReactNode;
}

export default function Highlight({
  title,
  sectionTitle = 'Courses',
  description,
  image,
  video,
  button,
  imageAlt,
  showTitleWithActions = false,
  titleWithActionsProps,
  backButton,
}: HighlightProps) {
  const { src } = useResponsiveImage({ url: image || '', alternativeText: imageAlt || '' });

  const highlightWrapperClassName = 'w-screen ml-[calc(50%-50vw)] bg-light-green p-sh-16 md:p-sh-60';

  const renderTitle = () => {
    if (showTitleWithActions && titleWithActionsProps) {
      return (
        <TitleWithActions
          title={title}
          entity={titleWithActionsProps.entity}
          entityId={titleWithActionsProps.entityId}
          category={titleWithActionsProps.category}
          moduleId={titleWithActionsProps.moduleId}
          lessonId={titleWithActionsProps.lessonId}
          like={titleWithActionsProps.like ?? false}
          favorite={titleWithActionsProps.favorite}
          type={titleWithActionsProps.type}
          textSize={titleWithActionsProps.textSize}
          knowledgeBaseTypeId={titleWithActionsProps.knowledgeBaseTypeId}
        />
      );
    }
    return <h1 className="font-haffer text-sh-30-38 font-bold">{title}</h1>;
  };

  if (video) {
    return (
      <div className={highlightWrapperClassName}>
        <AnimatedFadeInComponent>
          <Container>
            <div className="flex flex-col">
              {backButton && <div className="mb-sh-30 pl-sh-15">{backButton}</div>}
              <h1 className="mb-sh-30 font-haffer text-sh-30-38 font-bold">{sectionTitle}</h1>
            </div>
            <div className="col-span-12">
              <div className="relative flex flex-col gap-sh-15">
                <DefaultContentTemplateEmbedded
                  className="mindful-video !my-0"
                  fitContent
                  // @ts-expect-error DEPRECATED_STRAPI_ATTRIBUTES
                  component={{
                    id: 'DefaultContentTemplateEmbedded',
                    link: video,
                    provider: GENERATED.Enum_Componentcommonembedded_Provider.Youtube,
                  }}
                />
                {renderTitle()}
                <p className="mt-0 font-haffersq text-sh-18-28">{description}</p>
                {button}
              </div>
            </div>
          </Container>
        </AnimatedFadeInComponent>
      </div>
    );
  }

  return (
    <div className={highlightWrapperClassName}>
      <AnimatedFadeInComponent>
        <Container>
          <div className="flex flex-col">
            {backButton && <div className="mb-sh-30 pl-sh-15">{backButton}</div>}
            <h1 className="mb-sh-30 font-haffer text-sh-30-38 font-bold">{sectionTitle}</h1>
          </div>
          <div className="col-span-12 flex flex-col items-start">
            <div className="grid grid-cols-1 gap-sh-30 md:grid-cols-[1fr_1fr]">
              <div className="highlight-image-border flex aspect-video justify-center overflow-hidden bg-people-gray-200">
                <img
                  className="h-full w-[104.4%] min-w-full object-cover object-left"
                  src={src || ''}
                  alt={imageAlt || title}
                />
              </div>
              <div>
                {renderTitle()}
                <p className="mb-sh-15 mt-2 font-haffersq text-sh-18-28">{description}</p>
                {button}
              </div>
            </div>
          </div>
        </Container>
      </AnimatedFadeInComponent>
    </div>
  );
}
